import Footer from '@/components/Footer';
import RightContent from '@/components/RightContent';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { history, RequestConfig, RunTimeLayoutConfig, useQueryClient } from '@umijs/max';
import { App as AntdApp, Button, Result, Spin } from 'antd';
import React, { useEffect } from 'react';
import defaultSettings from '../config/defaultSettings';
import darkLogo from '../public/logo_black.png';
import whiteLogo from '../public/logo_white.png';
import { errorConfig } from './requestErrorConfig';
import {
  currentUser as queryCurrentUser,
  getUserSettings,
} from './services/impayment-api/user';
import { CURRENT_USER_QUERY_KEY, LOGIN_PATH } from './utils/constants';
import { handleTokenExpiration, isTokenValid } from './utils/utils';

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { chunkLoadError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { chunkLoadError: false };
  }

  static getDerivedStateFromError(error: Error) {
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
      return { chunkLoadError: true };
    }
    return null;
  }

  render() {
    if (this.state.chunkLoadError) {
      const isChinese = (localStorage.getItem('umi_locale') || navigator.language).startsWith('zh');
      return (
        <Result
          status="500"
          title={isChinese ? '页面加载失败' : 'Failed to load page'}
          subTitle={
            isChinese
              ? '页面资源加载失败，可能是网络问题或版本更新导致。请刷新页面重试。'
              : 'This page could not be loaded. It may be due to a network issue or a recent update. Please refresh to try again.'
          }
          extra={
            <Button type="primary" onClick={() => window.location.reload()}>
              {isChinese ? '刷新' : 'Refresh'}
            </Button>
          }
        />
      );
    }
    return this.props.children;
  }
}

const QueryClientSync = ({ currentUser }: { currentUser?: USER_API.User }) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (currentUser) {
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, currentUser);
    } else {
      queryClient.removeQueries({ queryKey: CURRENT_USER_QUERY_KEY, exact: true });
    }
  }, [currentUser, queryClient]);

  return null;
};

async function fetchUserInfo(): Promise<USER_API.User | undefined> {
  if (!isTokenValid()) {
    handleTokenExpiration();
    return undefined;
  }

  try {
    const res = await queryCurrentUser({ skipErrorHandler: true });
    return res;
  } catch (error) {
    handleTokenExpiration();
    return undefined;
  }
}

async function fetchUserSettings(): Promise<USER_API.UserSettingsType | undefined> {
  try {
    return await getUserSettings({ skipErrorHandler: true });
  } catch (error) {
    return undefined;
  }
}

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: USER_API.User;
  userSettings?: USER_API.UserSettingsType;
  loading?: boolean;
  fetchUserInfo?: () => Promise<USER_API.User | undefined>;
  fetchUserSettings?: () => Promise<USER_API.UserSettingsType | undefined>;
}> {
  const currentUser = await fetchUserInfo();
  const userSettings = currentUser ? await fetchUserSettings() : undefined;
  return {
    fetchUserInfo,
    fetchUserSettings,
    currentUser,
    userSettings,
    settings: defaultSettings,
  };
}

// ProLayout APIs supported https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    rightContentRender: () => <RightContent />,
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser?.email && location.pathname !== LOGIN_PATH) {
        history.push(LOGIN_PATH);
      }

      if (!isTokenValid() && initialState?.currentUser) {
        setInitialState({ ...initialState, currentUser: undefined });
        handleTokenExpiration();
      }
    },
    links: [],
    logo: initialState?.settings?.navTheme === 'realDark' ? whiteLogo : darkLogo,
    menuHeaderRender: undefined,
    // Custom 403 page
    // unAccessible: <div>unAccessible</div>,
    // Add a loading state
    childrenRender: (children) => {
      const content = initialState?.loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <>{children}</>
      );

      return (
        <ErrorBoundary>
          <AntdApp>
            <QueryClientSync currentUser={initialState?.currentUser} />
            {content}
          </AntdApp>
        </ErrorBoundary>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request configuration, you can configure error handling
 * It provides a unified network request and
 * error handling scheme based on the useRequest of axios and ahooks.
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
  baseURL: API_URL,
};
